import React, { useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import columns from "../data/Column";
import "../App.css";
import html2pdf from "html2pdf.js";
import { Tooltip } from "@mui/material";

const DataTable = ({ data }) => {
  // Add tooltip rendering to columns for mobile
  const columnsWithTooltip = columns.map((col) => ({
    ...col,
    renderHeader: (params) => (
      <Tooltip title={col.headerName} arrow placement="top">
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          cursor: 'pointer'
        }}>
          {col.headerName}
        </div>
      </Tooltip>
    ),
  }));

  const handleExportAllColumns = () => {
    const numberOfResults = data.length;
    const searchDate = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const fileName = `VoterSearch_${numberOfResults}_${searchDate}.csv`;

    // Add custom logic here for exporting data with the specified file name
    // For example, you can use a library like FileSaver.js to trigger the download

    // Below is a simple example using Blob and a download link
    const csvData = [
      columns.map((column) => column.field).join(","),
      ...data.map((row) =>
        columns.map((column) => row[column.field]).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, fileName);
    } else {
      // Others
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const handlePrintPDF = async () => {
    try {
      setIsLoading(true);

      // Get all active columns including ID_Number
      const activeColumns = columns.filter(col => {
        // Include all columns that are not commented out
        return true;
      });

      const container = document.createElement("div");
      container.style.width = "100%";
      container.style.fontFamily = "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif";
      container.style.fontSize = "9px";
      container.style.color = "#333";

      // Create table with all columns
      let tableHTML = `
        <table style="width: 100%; border-collapse: collapse; table-layout: auto; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <thead>
            <tr style="background: linear-gradient(to bottom, #4a5568 0%, #2d3748 100%); color: white;">
      `;

      // Add headers with better styling
      activeColumns.forEach((col, index) => {
        const isFirstCol = index === 0;
        const isLastCol = index === activeColumns.length - 1;
        tableHTML += `
          <th style="
            border: 1px solid #cbd5e0; 
            padding: 6px 4px; 
            font-size: 8px; 
            font-weight: 600; 
            text-align: center; 
            word-wrap: break-word;
            ${isFirstCol ? 'border-top-left-radius: 4px;' : ''}
            ${isLastCol ? 'border-top-right-radius: 4px;' : ''}
          ">${col.headerName}</th>
        `;
      });

      tableHTML += `
            </tr>
          </thead>
          <tbody>
      `;

      // Add data rows with alternating colors
      for (let index = 0; index < data.length; index++) {
        const row = data[index];
        const rowColor = index % 2 === 0 ? '#ffffff' : '#f7fafc';
        
        tableHTML += `<tr style="background-color: ${rowColor};">`;
        
        activeColumns.forEach(col => {
          let value = row[col.field] || '';
          
          // Apply valueGetter if exists
          if (col.valueGetter && typeof col.valueGetter === 'function') {
            value = col.valueGetter({ value: row[col.field] });
          }
          
          tableHTML += `
            <td style="
              border: 1px solid #e2e8f0; 
              padding: 5px 4px; 
              font-size: 8px; 
              word-wrap: break-word; 
              overflow-wrap: break-word;
              text-align: center;
              vertical-align: middle;
            ">${value}</td>
          `;
        });
        
        tableHTML += `</tr>`;

        // Yield to event loop periodically
        if (index % 50 === 0) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      tableHTML += `
          </tbody>
        </table>
      `;

      container.innerHTML = tableHTML;

      const pdfOptions = {
        margin: [8, 3, 8, 3],
        filename: "VoterData.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "landscape",
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().from(container).set(pdfOptions).save();
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="h-100 w-full overflow-auto">
        <DataGrid
          rows={data}
          columns={columnsWithTooltip}
          pageSize={5000}
          pagination
          disableSelectionOnClick
          disableColumnSelector
          disableDensitySelector
          getRowId={(row) => row["ID_Number"]}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            '& .MuiTablePagination-root': {
              '@media (max-width: 768px)': {
                '.MuiTablePagination-toolbar': {
                  display: 'flex',
                  flexWrap: 'wrap',
                  padding: '8px 8px',
                  minHeight: 'auto',
                },
                '.MuiTablePagination-spacer': {
                  display: 'none',
                },
                '.MuiTablePagination-selectLabel': {
                  display: 'inline-block !important',
                  margin: '0 4px 0 0',
                },
                '.MuiTablePagination-select': {
                  display: 'inline-block !important',
                },
                '.MuiTablePagination-displayedRows': {
                  margin: '0 8px',
                },
                '.MuiInputBase-root': {
                  display: 'inline-flex !important',
                  marginRight: '8px',
                },
                '.MuiTablePagination-actions': {
                  marginLeft: 'auto',
                }
              }
            }
          }}
          components={{
            Toolbar: () => (
              <div className="p-4">
                <div className="flex gap-2 justify-end">
                  <button
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm md:text-base flex-1 md:flex-none"
                    onClick={handleExportAllColumns}
                  >
                    Export All Columns
                  </button>
                  <button
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm md:text-base flex-1 md:flex-none"
                    onClick={handlePrintPDF}
                  >
                    Print PDF
                  </button>
                </div>
                {isLoading && <div className="mt-2 text-sm md:text-base">Downloading PDF...</div>}
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default DataTable;
